import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity, Send, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { runRequest, DT_STR as S, type ConsoleResult, type HttpMethod } from './devtoolsApi'

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

/**
 * A27.01 — API-консоль. Sends an arbitrary admin API request through the shared
 * client and pretty-prints the response. FUNCTIONAL (hits real endpoints) — the
 * caller's admin session/permissions apply. A27.02 request-inspector is folded
 * in via the raw response view.
 */
export function ApiConsoleScreen() {
  const [method, setMethod] = useState<HttpMethod>('GET')
  const [path, setPath] = useState('/admin/me')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ConsoleResult | null>(null)

  const send = async () => {
    setBusy(true)
    setResult(await runRequest(method, path.trim(), body))
    setBusy(false)
  }

  return (
    <>
      <PageHeader
        title={S.console}
        screenId="A27.01"
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/devtools/cache"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Trash2 size={13} />
              {S.cache}
            </Link>
            <Link
              to="/devtools/health"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Activity size={13} />
              {S.health}
            </Link>
          </div>
        }
      />
      <div className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as HttpMethod)}
            className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary outline-none"
          >
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/admin/..."
            spellCheck={false}
            className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 font-mono text-sm text-primary outline-none focus:border-border-strong"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy || !path.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Send size={14} />
            {busy ? S.running : S.send}
          </button>
        </div>
        {method !== 'GET' && method !== 'DELETE' && (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            spellCheck={false}
            rows={5}
            placeholder={`${S.body}: { }`}
            className="w-full resize-y rounded-md border border-border-default bg-surface-1 p-3 font-mono text-sm text-primary outline-none focus:border-border-strong"
          />
        )}
        {result && (
          <div>
            <div className="mb-1 text-sm font-medium text-secondary">
              {S.response} · {result.ok ? '✅' : '⚠️'}
            </div>
            <pre className="max-h-[420px] overflow-auto rounded-md border border-border-default bg-surface-1 p-3 font-mono text-xs text-primary">
              {typeof result.data === 'string'
                ? result.data
                : JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </>
  )
}
